/**
 * BreakpointListener utility class that listens for changes in the viewport width and
 * notifies listeners of the current breakpoint
 * @class BreakpointListener
 * @example
 * const breakpointListener = BreakpointListener.getInstance()
 * breakpointListener.addListener((matches) => {
 *  console.log(matches)
 * })
 */
export default class BreakpointListener {
	/**
	 * Viewport breakpoints
	 */
	static BREAKPOINTS = {
		SMALL: 0,
		MEDIUM: 768,
		MEDIUM_LARGE: 996,
		LARGE: 1440,
		XLARGE: 1920
	}

	/**
	 * Media query strings for each breakpoint
	 * Note: these media queries match those in the _breakpoints.scss file
	 */
	static BREAKPOINT_MAP = {
		SMALL: `( min-width: ${BreakpointListener.BREAKPOINTS.SMALL}px )`,
		SMALL_MAX: `( max-width: ${
			BreakpointListener.BREAKPOINTS.MEDIUM - 1
		}px )`,
		SMALL_ONLY: `( (min-width: ${
			BreakpointListener.BREAKPOINTS.SMALL
		}px) and (max-width: ${BreakpointListener.BREAKPOINTS.MEDIUM - 1}px) )`,
		MEDIUM: `( min-width:  ${BreakpointListener.BREAKPOINTS.MEDIUM}px )`,
		MEDIUM_LARGE: `( min-width:  ${BreakpointListener.BREAKPOINTS.MEDIUM_LARGE}px )`,
		MEDIUM_MAX: `( max-width: ${
			BreakpointListener.BREAKPOINTS.LARGE - 1
		}px )`,
		MEDIUM_LARGE_MAX: `( max-width: ${
			BreakpointListener.BREAKPOINTS.MEDIUM_LARGE - 1
		}px )`,
		MEDIUM_ONLY: `((min-width: ${
			BreakpointListener.BREAKPOINTS.MEDIUM
		}px) and (max-width: ${BreakpointListener.BREAKPOINTS.LARGE - 1}px))`,
		LARGE: `( min-width: ${BreakpointListener.BREAKPOINTS.LARGE}px )`,
		LARGE_MAX: `( max-width: ${
			BreakpointListener.BREAKPOINTS.XLARGE - 1
		}px )`,
		LARGE_ONLY: `((min-width: ${
			BreakpointListener.BREAKPOINTS.LARGE
		}px) and (max-width: ${BreakpointListener.BREAKPOINTS.XLARGE - 1}px))`,
		XLARGE: `( min-width: ${BreakpointListener.BREAKPOINTS.XLARGE}px )`
	}

	/**
	 * Singleton instance of the BreakpointListener class
	 * @returns {BreakpointListener}
	 */
	static getInstance() {
		if (!this.instance) {
			this.instance = new BreakpointListener()
		}
		return this.instance
	}

	/**
	 * Creates an instance of BreakpointListener
	 */
	constructor() {
		this.queries = BreakpointListener.BREAKPOINT_MAP
		this.queryMatch = null
		this.mediaQueryLists = {}
		this.keys = Object.keys(BreakpointListener.BREAKPOINT_MAP)
		this.isAttached = false
		this.listeners = []
		this.createMatchMedia()
	}

	/**
	 * Attaches event listeners to the media query lists and invokes the listeners with the current breakpoint
	 * @param {Function} listener - Callback function that is invoked when the breakpoint changes
	 */
	addListener(listener) {
		if (typeof listener === 'function') {
			this.listeners.push(listener)

			// If the listener is added after the media query has been evaluated, invoke the listener immediately
			if (this.isAttached) {
				listener(this.queryMatch)
			}
		}
	}

	/**
	 * Removes the listener from the list of listeners
	 * @param {Funciton} listener - Callback function that is invoked when the breakpoint changes and should be removed
	 */
	removeListener(listener) {
		this.listeners = this.listeners.filter((l) => l !== listener)
	}

	/**
	 * Destroys the instance of BreakpointListener
	 */
	destroy() {
		this.listeners = []

		if (this.isAttached) {
			this.keys.forEach((media) => {
				if (typeof this.queries[media] === 'string') {
					this.mediaQueryLists[media].removeListener(
						this.handleQueryListener
					)
				}
			})
		}
	}

	/**
	 * Creates the media query lists and attaches event listeners to them
	 */
	createMatchMedia() {
		if (window && window.matchMedia) {
			const matches = {}
			this.keys.forEach((media) => {
				if (typeof this.queries[media] === 'string') {
					this.mediaQueryLists[media] = window.matchMedia(
						this.queries[media]
					)
					matches[media] = this.mediaQueryLists[media].matches
				} else {
					matches[media] = false
				}
			})
			this.queryMatch = matches
			this.isAttached = true
			this.keys.forEach((media) => {
				if (typeof this.queries[media] === 'string') {
					this.mediaQueryLists[media].addListener(this.handleQueryListener)
				}
			})
		}
	}

	/**
	 * Handles the event listener for the media query lists and invokes the listeners with the current breakpoint
	 */
	handleQueryListener = () => {
		const updatedMatches = this.keys.reduce((acc, media) => {
			acc[media] = !!(
				this.mediaQueryLists[media] && this.mediaQueryLists[media].matches
			)
			return acc
		}, {})
		this.queryMatch = updatedMatches

		if (this.listeners && this.listeners.length > 0) {
			this.listeners.forEach((listener) => {
				listener(this.queryMatch)
			})
		}
	}

	getCurrentBreakpoint() {
		return this.queryMatch
	}
}
